import React, { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { Topic, PatternDraft, Note } from '@/types'
import { TopicType } from '@/utils/constants'
import { useGetTopicDetails } from '@/hooks/api/dashboard'
import {
  StartRenderer,
  RestorePattern,
  ResetRenderer,
  SetTopicType,
    Reset3DScene,
} from '@/pages/pattern/renderer/main'
import { Assets } from '@/utils/pattern-assets'

interface BabylonPreviewProps {
  topicID: string
  height?: string | number
  onLoad?: () => void
}

const BabylonPreview: React.FC<BabylonPreviewProps> = ({ topicID, height = '100%', onLoad }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const getTopicDetails = useGetTopicDetails()
  const rendererInitialized = useRef(false)

  useEffect(() => {
    try {
      if (typeof Reset3DScene === 'function') {
        Reset3DScene()
      } else {
        ResetRenderer() // Fallback if you haven't updated main.js yet
      }
    } catch (e) {
      console.warn('Error resetting 3D scene:', e)
    }
    // Delay initialization to avoid conflicts with other Babylon instances
    const timer = setTimeout(() => {
      initializePreview()
    }, 300)

    return () => {
      clearTimeout(timer)
      // Cleanup on unmount
      try {
        if (typeof Reset3DScene === 'function') {
          Reset3DScene()
        }
      } catch (e) {
        console.warn('Error cleaning up Babylon renderer:', e)
      }
      // if (rendererInitialized.current) {
      //   try {
      //     ResetRenderer()
      //   } catch (e) {
      //     console.warn('Error cleaning up Babylon renderer:', e)
      //   }
      //   rendererInitialized.current = false
      // }
    }
  }, [topicID])

  const initializePreview = async () => {
    if (!canvasRef.current) return

    try {
      setLoading(true)

      // Get topic data
      const topic = await getTopicDetails.mutateAsync({ topicID })
      if (!topic) {
        setError(true)
        return
      }

      // Parse pattern data from notes
      const defaultTemplateID =
        topic.topicType == TopicType.PATTERN.toString()
          ? 2
          : topic.topicType == TopicType.GALLERY.toString()
          ? 400
          : 2

      const draft: PatternDraft = { templateID: defaultTemplateID }
      const uniqueNotes = new Map<string, Note>()
      
      topic.notes.forEach((note: Note) => {
        if (
          note.noteType !== '1' &&
          note.noteType !== '8' &&
          note.noteType !== '9' &&
          note.noteText !== 'null' &&
          note.noteText != null
        ) {
          const existing = uniqueNotes.get(note.noteName)

          if (!existing || (note.dateUpdated && note.dateUpdated > existing.dateUpdated)) {
            uniqueNotes.set(note.noteName, note)
          }
          uniqueNotes.forEach((note) => {
            try {
              draft[note.noteName as keyof PatternDraft] = JSON.parse(note.noteText)
            } catch (e) {
              console.warn('Failed to parse note:', note.noteName, e)
            }
          })
        }
      })

      // Initialize Babylon renderer in preview mode
      if (!rendererInitialized.current) {
        try {
          StartRenderer({
            canvas: canvasRef.current,
            assets: Assets,
            mode: 'preview',
            canGrab: false,
            onEdit: () => {},
            onPatternRestore: () => {},
            onAssetsUpdate: () => {},
            toggleSliderVisibility: () => {},
            updateSliderFromAccentScale: () => {},
            updateSliderFromAccentNumber: () => {},
            updateSliderFromAccentRotation: () => {},
            updateSliderFromPrintTiling: () => {},
            updateSliderFromCameraHorizontalPosition: () => {},
            setMode: () => {},
            addNote: () => {},
          })
          rendererInitialized.current = true
        } catch (rendererError) {
          console.error('Failed to initialize Babylon renderer:', rendererError)
          setError(true)
          setLoading(false)
          return
        }
      }

      // Set topic type and restore pattern
      SetTopicType(topic.topicType)

      // Wait longer for materials to load before restoring pattern
      setTimeout(() => {
        try {
          RestorePattern(draft, false, () => {
            setLoading(false)
            onLoad?.()
          })
        } catch (restoreError) {
          console.error('Failed to restore pattern:', restoreError)
          setError(true)
          setLoading(false)
        }
      }, 100) // Increased timeout to allow materials to load
    } catch (e) {
      console.error('Failed to load pattern preview:', e)
      setError(true)
      setLoading(false)
    }
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontSize: '14px',
        }}
      >
        Preview unavailable
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            zIndex: 10,
          }}
        >
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      )}
      <canvas 
        key={topicID}
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          touchAction: 'none',
        }}
      />
    </Box>
  )
}

export default BabylonPreview
